import {
  Box,
  Divider,
  IconChevron,
  IconSocialFacebook,
  IconSocialInstagram,
  IconSocialLinkedIn,
  Link,
  Stack,
  Text,
} from 'braid-design-system';
import {
  useState,
  type HTMLAttributeAnchorTarget,
  type ReactNode,
} from 'react';

import { createDotNetLinkUrl } from 'src/utils';

import { FirstNationsBanner } from './FirstNationsBanner';

import * as styles from './SiteWideFooterLinksSection.css';

export const SiteWideFooterLinksSection = () => (
  <Stack space="xlarge">
    <Box className={styles.container}>
      <Stack space="large">
        <FooterHeading>Buyers</FooterHeading>

        <Stack space="large">
          <FooterLink href={createDotNetLinkUrl('/businesses-for-sale')}>
            Business search
          </FooterLink>
          <FooterLink href={createDotNetLinkUrl('/profile/my-details')}>
            Profile
          </FooterLink>
          <FooterLink href={createDotNetLinkUrl('/profile/saved-businesses')}>
            Saved businesses
          </FooterLink>
          <FooterLink href={createDotNetLinkUrl('/profile/saved-searches')}>
            Saved searches
          </FooterLink>
          <FooterLink
            href={createDotNetLinkUrl(
              '/business-resources/category/buying-a-business',
            )}
          >
            Buying advice
          </FooterLink>
          <FooterLink href={createDotNetLinkUrl('/safe-searching')}>
            Online safety
          </FooterLink>
        </Stack>
      </Stack>

      <Stack space="large">
        <FooterHeading>Advertisers</FooterHeading>

        <Stack space="large">
          <FooterLink href={createDotNetLinkUrl('/sell-your-business')}>
            Selling a business
          </FooterLink>
          <FooterLink
            href={createDotNetLinkUrl(
              '/business-resources/category/selling-a-business',
            )}
          >
            Selling advice
          </FooterLink>
          <FooterLink href="https://www.seek.com.au" target="_top">
            Find staff
          </FooterLink>
        </Stack>
      </Stack>

      <Stack space="large">
        <FooterHeading>About us</FooterHeading>

        <Stack space="large">
          <FooterLink href={createDotNetLinkUrl('/about-us')}>
            About SEEK Business
          </FooterLink>
          <FooterLink href={createDotNetLinkUrl('/terms-and-conditions')}>
            Terms of use
          </FooterLink>
          <FooterLink href={createDotNetLinkUrl('/privacy-policy')}>
            Privacy
          </FooterLink>
          <FooterLink href={createDotNetLinkUrl('/collection-notice')}>
            Collection notice
          </FooterLink>
          <SimpleAccordion label="SEEK sites">
            <Box paddingLeft="small">
              <Stack space="large">
                <FooterLink href="https://www.seek.com.au" target="_top">
                  SEEK
                </FooterLink>
                <FooterLink href="https://www.volunteer.com.au" target="_top">
                  Volunteering
                </FooterLink>
                <FooterLink href="https://au.jora.com" target="_top">
                  Jora
                </FooterLink>
              </Stack>
            </Box>
          </SimpleAccordion>
          <FooterLink href={createDotNetLinkUrl('/sitemap')}>
            Site map
          </FooterLink>
        </Stack>
      </Stack>

      <Stack space="large">
        <FooterHeading>Connect with us</FooterHeading>

        <Stack space="large">
          <FooterLink href={createDotNetLinkUrl('/contact-us')}>
            Contact us
          </FooterLink>
          <Link href="http://www.linkedin.com/company/4832619" target="_blank">
            <Text tone="secondary" size="small">
              <IconSocialLinkedIn /> LinkedIn
            </Text>
          </Link>
          <Link href="https://www.instagram.com/seekbusiness" target="_blank">
            <Text tone="secondary" size="small">
              <IconSocialInstagram /> Instagram
            </Text>
          </Link>
          <Link href="https://www.facebook.com/seekbusiness" target="_blank">
            <Text tone="secondary" size="small">
              <IconSocialFacebook /> Facebook
            </Text>
          </Link>
        </Stack>
      </Stack>
    </Box>

    <Divider />

    <FirstNationsBanner />

    <Divider />

    <Text
      tone="secondary"
      size="small"
      data={{ testid: 'footer-copyright-mobile' }}
    >
      {copyright}
    </Text>
  </Stack>
);

interface SimpleAccordionProps {
  label: string;
  children: ReactNode;
}

const SimpleAccordion = (props: SimpleAccordionProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const button = (
    <Box
      display="flex"
      alignItems="center"
      component="button"
      textAlign="left"
      gap="xxsmall"
      onClick={() => setIsOpen((prev) => !prev)}
    >
      <Text tone="secondary" size="small">
        {props.label}
      </Text>

      <IconChevron size="xsmall" direction={isOpen ? 'up' : 'down'} />
    </Box>
  );

  if (isOpen) {
    return (
      <Stack space="large">
        {button}
        <Box>{props.children}</Box>
      </Stack>
    );
  }

  return button;
};

interface FooterHeadingProps {
  children: string;
}

const FooterHeading = (props: FooterHeadingProps) => (
  <Text weight="strong" component="h4">
    {props.children}
  </Text>
);

interface FooterLinkProps {
  href: string;
  children: string;
  target?: HTMLAttributeAnchorTarget;
}

const FooterLink = (props: FooterLinkProps) => (
  <Text tone="secondary" size="small">
    <Link href={props.href} target={props.target}>
      {props.children}
    </Link>
  </Text>
);

const copyright = '© SEEK Business. All rights reserved.';
